import React, { useMemo, useState } from 'react'
import FeatherIcon from '@ovaeasy/react-native-vector-icons/Feather'
import Tooltip from 'react-tooltip'

import RoundedButton from '@app/ui/rounded-button/RoundedButton'
import theme from '@app/ui/theme'

import { ProjectCourse } from '../../../types/Project'
import { LRCourse } from '../../../types/Clubs'
import { MiProjectStatus } from '@app/evaluation-core/types'
import { EvaluationDoc } from '../../../pages/CreateRatingPage/CreateRatingPage'
import { useApi } from '@common/hooks/useApi'
import EvalStatusIndicator from './EvalStatusIndicator'
import { EvalStatus } from '@app/courses/domain/ratingStatus'
import DateTimeView from '../../atoms/DateTimeView'
import GenderIcon from '../../atoms/GenderIcon'
import CourseScorecardModal from '../CourseScorecardModal/CourseScorecardModal'
import * as S from './mobile.styled'

type CourseListItemProps = {
  branchId?: string
  hasActiveEval: boolean
  startEvaluation: () => Promise<void>
  buttonText: string
  projectCourse: Partial<ProjectCourse>
  clubId: string
  projectStatus?: MiProjectStatus
  lrCourse?: LRCourse
  courseRateable?: boolean
}

const MobileCourseListItem: React.FC<CourseListItemProps> = ({
  branchId,
  projectCourse,
  lrCourse,
  clubId,
  startEvaluation,
  buttonText,
  courseRateable,
}) => {
  const [scorecardModalVisible, setScorecardModalVisible] = useState(false)

  const holeIds = useMemo(() => {
    return (projectCourse.holes ?? [])
      .map((h) => h.physicalID)
      .filter((id): id is string => Boolean(id))
  }, [projectCourse.holes])

  const { data: evals, isLoading } = useApi<EvaluationDoc[]>(
    `/evaluations/import?holeId=${holeIds.join(',')}&clubId=${
      clubId ?? lrCourse?.golfClubID
    }`,
    undefined
  )

  const highestStatus = useMemo(() => {
    return evals
      ?.filter((e) => e.status !== EvalStatus.FINALIZED)
      ?.sort((a, b) => b.status - a.status)
      .at(0)?.status
  }, [evals])

  const latestFinalizedEval = useMemo(() => {
    return evals
      ?.filter((e) => e.finalizedAt !== undefined && e.finalizedAt !== null)
      .sort((a, b) => {
        if (!a.finalizedAt && !b.finalizedAt) return 0
        if (!a.finalizedAt) return 1
        if (!b.finalizedAt) return -1
        return (
          new Date(b.finalizedAt).getTime() -
          new Date(a.finalizedAt).getTime()
        )
      })
      .at(0)
  }, [evals])

  return (
    <>
      <CourseScorecardModal
        branchId={branchId}
        projectCourse={projectCourse}
        isVisible={scorecardModalVisible}
        closeFn={() => setScorecardModalVisible(false)}
      />

      <S.CourseCard>
        <S.CourseMain>
          <S.CourseHeader>
            <S.NameRow>
              {courseRateable !== undefined && !courseRateable && (
                <>
                  <a data-for="not-rateable" data-tip="Please check LiveCR.">
                    <FeatherIcon
                      id="course-not-rateable-tooltip"
                      name="alert-triangle"
                      color={theme.colors.warning}
                      fill={theme.colors.warning}
                      size={22}
                      style={{ marginRight: 8 }}
                    />
                  </a>
                  <Tooltip
                    id="not-rateable"
                    className="not-rateable"
                    delayHide={1000}
                    textColor="white"
                    backgroundColor={theme.colors.primary}
                    effect="solid"
                    place="right"
                  />
                </>
              )}

              <S.CourseName title={projectCourse.name}>
                {projectCourse.name}
              </S.CourseName>
            <S.TeeRow>
              {(projectCourse.tees ?? []).map((tee) => (
                <S.TeePill key={tee.id}>
                  {['1', '2', '3'].includes(tee.gender) && (
                    <GenderIcon genderKey={tee.gender} />
                  )}
                  <S.TeeName>{tee.name}</S.TeeName>
                </S.TeePill>
              ))}
            </S.TeeRow>
            </S.NameRow>

          </S.CourseHeader>

          <S.InfoRow>
            <S.InfoItem>
              <S.InfoLabel>Status</S.InfoLabel>
              <S.InfoValue>
                {isLoading || !evals || !highestStatus ? (
                  <S.Dash>-</S.Dash>
                ) : (
                  <EvalStatusIndicator status={highestStatus} />
                )}
              </S.InfoValue>
            </S.InfoItem>

            <S.InfoItem>
              <S.InfoLabel>Last finalized</S.InfoLabel>
              <S.InfoValue>
                {isLoading || !evals || !latestFinalizedEval ? (
                  <S.Dash>-</S.Dash>
                ) : (
                  <EvalStatusIndicator
                    status={latestFinalizedEval.status as EvalStatus}
                    finalizedAt={latestFinalizedEval.finalizedAt}
                  />
                )}
              </S.InfoValue>
            </S.InfoItem>

            <S.InfoItem>
              <S.InfoLabel>Holes</S.InfoLabel>
              <S.InfoValue>
                <S.HoleBadge>{projectCourse.holeCount ?? '-'}</S.HoleBadge>
              </S.InfoValue>
            </S.InfoItem>

            <S.InfoItem>
              <S.InfoLabel>LiveCR Last synced</S.InfoLabel>
              <S.InfoValue>
                {lrCourse?.branchStatus.latestTimeSyncedDate ? (
                  <DateTimeView
                    dateString={lrCourse.branchStatus.latestTimeSyncedDate}
                  />
                ) : (
                  <S.Dash>-</S.Dash>
                )}
              </S.InfoValue>
            </S.InfoItem>
          </S.InfoRow>
        </S.CourseMain>

        <S.Actions>
          <RoundedButton
            onPress={() => setScorecardModalVisible(true)}
            title="Scorecard"
            testId="btn-scorecard"
            disabled={!projectCourse}
          />
          <RoundedButton
            isDefault={true}
            onPress={startEvaluation}
            title={buttonText}
            testId="btn-manage-rating"
            disabled={courseRateable !== undefined && !courseRateable}
          />
        </S.Actions>
      </S.CourseCard>
    </>
  )
}

export default MobileCourseListItem